import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  Stack,
  Chip,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  CheckCircleOutline as CheckIcon,
  DescriptionOutlined as TemplateIcon,
  CloudOffOutlined as OfflineIcon,
  PhotoLibraryOutlined as PhotoIcon,
  PictureAsPdfOutlined as PdfIcon,
  MyLocationOutlined as GpsIcon,
  FactCheckOutlined as ApprovalIcon,
  NotificationsActiveOutlined as NotifyIcon,
  ReceiptLongOutlined as BillingIcon,
  HistoryOutlined as AuditIcon,
  ShieldOutlined as ShieldIcon,
  VerifiedOutlined as VerifiedIcon,
  BoltOutlined as BoltIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Seo, { SITE_URL } from '../components/Seo';
import { trackCta } from '../utils/analytics';
import ShareButtons from '../components/ShareButtons';

const SectionLabel = ({ children, light }) => (
  <Typography
    variant="overline"
    sx={{ color: light ? 'secondary.light' : 'secondary.dark', display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
);

const heroStats = [
  { value: '<3s', label: 'Report generation' },
  { value: '~90%', label: 'Less report-writing time' },
  { value: '2×', label: 'Inspector capacity' },
  { value: '1,713', label: 'Automated tests' },
];

const workflow = [
  'Client Request',
  'Inspection Created',
  'Inspector Assigned',
  'Field Capture',
  'Auto PDF Report',
  'Manager Approval',
  'Client Delivery',
  'Invoice',
];

const roles = [
  { role: 'Manager', platform: 'Web', desc: 'Configure report templates, approve or reject reports, manage users and pricing.' },
  { role: 'Office Staff', platform: 'Web', desc: 'Create and assign inspections, manage clients, deliver reports and invoices.' },
  { role: 'Inspector', platform: 'Mobile', desc: 'Capture data, photos and checklists in the field — even fully offline.' },
  { role: 'Client', platform: 'Web', desc: 'Self-service portal to view their own reports and invoices, read-only.' },
];

const capabilities = [
  { icon: <TemplateIcon />, title: 'No-Code Template Builder', desc: 'Design standardized report structures per service — fields, image slots, pass/fail checklists, data tables and disclaimers.' },
  { icon: <OfflineIcon />, title: 'Offline-First Field Capture', desc: 'Inspectors work on Android with no connectivity. Data stores locally and syncs automatically on reconnect.' },
  { icon: <PhotoIcon />, title: 'Reference Image Guidance', desc: 'Attach reference photos to each slot so inspectors capture exactly what is needed — less rework, more consistency.' },
  { icon: <PdfIcon />, title: 'Automated PDF Reports', desc: 'Branded, professional reports assembled on completion with zero manual formatting.' },
  { icon: <GpsIcon />, title: 'Site Verification', desc: 'Require a geotagged, timestamped selfie stamped with inspector name and GPS — tamper-evident proof of attendance.' },
  { icon: <ApprovalIcon />, title: 'Approval Workflow', desc: 'Managers review a queue, preview the PDF, and approve or reject with feedback for resubmission.' },
  { icon: <NotifyIcon />, title: 'Real-Time Notifications', desc: 'Assignment, completion, approval and rejection events trigger instant push notifications.' },
  { icon: <BillingIcon />, title: 'Client Portal & Billing', desc: 'Invoices auto-generate from sales orders with client-specific pricing per service.' },
  { icon: <AuditIcon />, title: 'Full Audit Trail', desc: 'Every action logged with user and timestamp for compliance and dispute resolution.' },
];

const inspectionServices = [
  'Pre-Dispatch Loading Inspection',
  'Factory Audit & Witness Analysis',
  'Discharge / Arrival Inspection',
  'Empty Container Condition Inspection',
  'During Production Inspection (DPI)',
  'Sampling & Weight Verification',
  'Dangerous Goods (DG/IMDG) Inspection',
  'Stock / Inventory Audit',
  'Packaging Compliance Inspection',
  'Tally & Quantity Verification',
];

const impactRows = [
  { metric: 'Inspections / month', before: '200–300', after: '500–600' },
  { metric: 'Report turnaround', before: '2–3 days', after: '< 1 hour' },
  { metric: 'Office staff for reports', before: '3–4 people', after: '1 person' },
];

const techStack = [
  'Node.js / Express', 'TypeScript', 'MongoDB Atlas', 'AWS S3',
  'React + Vite', 'React Native + Expo', 'PDFKit', 'JWT + RBAC',
];

const pricing = [
  { model: 'Per-Inspector License', structure: 'Recurring monthly per active inspector', best: 'Small–medium agencies' },
  { model: 'Per-Inspection Fee', structure: 'Usage-based pricing', best: 'High-volume agencies' },
  { model: 'Flat Enterprise', structure: 'Unlimited monthly', best: 'Large agencies (20+ inspectors)' },
  { model: 'License + Maintenance', structure: 'One-time + recurring support', best: 'Agencies wanting ownership' },
];

const Platform = () => {
  const navigate = useNavigate();

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'InspectIQ Platform',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Android',
    description:
      'The operating system for inspection agencies — digitizes the inspection lifecycle from client request and offline field capture to automated reports, approval, delivery and invoicing.',
    url: `${SITE_URL}/platform`,
    offers: { '@type': 'Offer', category: 'SaaS' },
    publisher: { '@id': `${SITE_URL}/#organization` },
  };

  return (
    <Box>
      <Seo
        title="InspectIQ Platform — Software for Inspection Agencies"
        description="The operating system for inspection agencies: offline field capture, automated PDF reports in under 3 seconds, approval workflow, client portal and invoicing."
        path="/platform"
        structuredData={softwareSchema}
      />
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg, #012637 0%, #01567a 100%)', color: 'white', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 820 }}>
            <SectionLabel light>InspectIQ Platform</SectionLabel>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.5rem' }, mb: 2.5 }}>
              The operating system for inspection agencies
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.88)', mb: 4, maxWidth: 680 }}
            >
              InspectIQ digitizes the entire inspection lifecycle — from client request and
              offline field capture to automated reports, approval, client delivery and
              invoicing — on one connected platform across mobile, web and cloud.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/contact')}
                sx={{ bgcolor: 'white', color: 'primary.main', px: 4, py: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
              >
                Request a Demo
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="#capabilities"
                sx={{ px: 4, py: 1.5, color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' } }}
              >
                See Capabilities
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero stats */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container sx={{ py: { xs: 4, md: 5 } }}>
            {heroStats.map((stat, i) => (
              <Grid item xs={6} md={3} key={i} sx={{ textAlign: 'center', py: { xs: 2, md: 0 }, borderRight: { md: i < heroStats.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none' } }}>
                <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.5rem' }, color: 'secondary.light', lineHeight: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)', px: 1 }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* See it in action */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <SectionLabel>See It In Action</SectionLabel>
          <Typography variant="h2" gutterBottom>Explore the platform without signing up</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <ShareButtons title="InspectIQ Platform" path="/platform" label="Share the platform" />
          </Box>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', borderTop: '3px solid', borderTopColor: 'secondary.main' }}>
              <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: 'rgba(1,86,122,0.08)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <PdfIcon />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>Sample Report</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                See a full, branded inspection report exactly as the platform generates it —
                metadata, checklist, cargo tally, photo evidence and site verification.
              </Typography>
              <Button variant="contained" endIcon={<ArrowIcon />} onClick={() => { trackCta('View Sample Report', 'platform'); navigate('/platform/demo-report'); }} sx={{ mt: 'auto', alignSelf: 'flex-start', px: 3 }}>
                View Sample Report
              </Button>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', borderTop: '3px solid', borderTopColor: 'secondary.main' }}>
              <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: 'rgba(1,86,122,0.08)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <BoltIcon />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>Interactive Walkthrough</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Step through a single inspection from request to delivery across all four
                roles — create, assign, capture, generate, approve and invoice.
              </Typography>
              <Button variant="contained" endIcon={<ArrowIcon />} onClick={() => { trackCta('Try the Demo', 'platform'); navigate('/platform/demo'); }} sx={{ mt: 'auto', alignSelf: 'flex-start', px: 3 }}>
                Try the Demo
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* The Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <SectionLabel>The Problem</SectionLabel>
            <Typography variant="h2" gutterBottom>
              Multi-billion-dollar operations still run on paper
            </Typography>
            <Typography variant="body1">
              Most third-party inspection agencies still rely on paper forms, photos
              scattered across WhatsApp, and manually typed Word reports. It is slow,
              inconsistent and impossible to audit at scale.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {[
                'Reports take 2–3 days and hours of manual formatting each',
                'Photos get lost across personal phones and chat groups',
                'No standardization, no audit trail, no real-time visibility',
                'Billing leakage from manual invoicing',
                'Office headcount scales linearly with inspection volume',
              ].map((pain, i) => (
                <Card key={i} sx={{ p: 2.5, bgcolor: '#f5f8fa' }}>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>{pain}</Typography>
                </Card>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* How it works */}
      <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>How It Works</SectionLabel>
            <Typography variant="h2" gutterBottom>One connected workflow, four roles</Typography>
          </Box>

          {/* Pipeline */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 6 }}>
            {workflow.map((step, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                <Box sx={{ px: 2, py: 1, bgcolor: 'white', border: '1px solid rgba(15,39,51,0.1)', borderRadius: 2, fontSize: '0.85rem', fontWeight: 600, color: 'primary.main' }}>
                  {step}
                </Box>
                {i < workflow.length - 1 && <ArrowIcon sx={{ fontSize: 16, color: 'secondary.main' }} />}
              </Stack>
            ))}
          </Box>

          {/* Roles */}
          <Grid container spacing={3}>
            {roles.map((r, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Chip label={r.platform} size="small" color="primary" sx={{ mb: 1.5 }} />
                  <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>{r.role}</Typography>
                  <Typography variant="body2" color="text.secondary">{r.desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Capabilities */}
      <Container id="capabilities" maxWidth="lg" sx={{ py: { xs: 7, md: 10 }, scrollMarginTop: '90px' }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>Core Capabilities</SectionLabel>
          <Typography variant="h2" gutterBottom>Everything an inspection agency needs</Typography>
        </Box>
        <Grid container spacing={3}>
          {capabilities.map((cap, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card sx={{ p: 3.5, height: '100%' }}>
                <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: 'rgba(1,86,122,0.08)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  {cap.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>{cap.title}</Typography>
                <Typography variant="body2" color="text.secondary">{cap.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Supported Inspection Services */}
      <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <SectionLabel>Supported Services</SectionLabel>
            <Typography variant="h2" gutterBottom>Pre-configured for common agency workflows</Typography>
            <Typography variant="body1" sx={{ maxWidth: 680, mx: 'auto' }}>
              Ready-to-use, fully customizable templates — plus a built-in product and
              chemical database that auto-fills UN code, hazard class and packing group
              for dangerous-goods accuracy.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {inspectionServices.map((svc, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ p: 2.5, height: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{svc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Business Impact */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>Business Impact</SectionLabel>
          <Typography variant="h2" gutterBottom>From cost center to competitive advantage</Typography>
        </Box>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5}>
              {[
                { icon: <BoltIcon />, text: '~90% reduction in report-writing time' },
                { icon: <VerifiedIcon />, text: 'Same-day delivery vs. 2–3 day turnaround' },
                { icon: <ShieldIcon />, text: 'Tamper-evident site verification and full audit trail' },
              ].map((item, i) => (
                <Stack key={i} direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'rgba(1,86,122,0.08)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.icon}
                  </Box>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>{item.text}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card sx={{ overflow: 'hidden' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr' }}>
                {['', 'Traditional', 'With InspectIQ'].map((h, i) => (
                  <Box key={i} sx={{ p: 2, bgcolor: 'primary.dark', color: 'white', fontWeight: 700, fontSize: '0.85rem', textAlign: i === 0 ? 'left' : 'center' }}>
                    {h}
                  </Box>
                ))}
                {impactRows.map((row, ri) => (
                  <React.Fragment key={ri}>
                    <Box sx={{ p: 2, borderTop: '1px solid rgba(15,39,51,0.08)', fontWeight: 600, color: 'text.primary', fontSize: '0.9rem' }}>{row.metric}</Box>
                    <Box sx={{ p: 2, borderTop: '1px solid rgba(15,39,51,0.08)', textAlign: 'center', color: 'text.secondary', fontSize: '0.9rem' }}>{row.before}</Box>
                    <Box sx={{ p: 2, borderTop: '1px solid rgba(15,39,51,0.08)', textAlign: 'center', color: 'primary.main', fontWeight: 700, fontSize: '0.9rem', bgcolor: 'rgba(1,86,122,0.04)' }}>{row.after}</Box>
                  </React.Fragment>
                ))}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', p: 2, color: 'text.secondary' }}>
                Illustrative figures for a 10-inspector agency. Break-even typically reached in month 2–3 of adoption.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Built for trust */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }}>
            <Grid item xs={12} md={6}>
              <SectionLabel light>Security &amp; Compliance</SectionLabel>
              <Typography variant="h3" gutterBottom sx={{ color: 'white' }}>Built to enterprise security standards</Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {[
                  'JWT authentication with token refresh & session expiry',
                  'Role-based access control enforced on every endpoint',
                  'bcrypt password hashing, rate limiting & Helmet headers',
                  'NoSQL injection, path-traversal & malicious upload prevention',
                  'Complete, immutable audit trail',
                ].map((item, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckIcon sx={{ color: 'secondary.light', fontSize: 20, mt: 0.3 }} />
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionLabel light>Quality &amp; Reliability</SectionLabel>
              <Typography variant="h3" gutterBottom sx={{ color: 'white' }}>Validated by 1,700+ automated tests</Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
                Property-based, unit, integration, security and performance coverage —
                with zero TypeScript errors and zero lint warnings across all packages.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { value: '1,713', label: 'Automated tests' },
                  { value: '124', label: 'Test suites' },
                  { value: '50+', label: 'Concurrent submissions' },
                  { value: '<2s', label: 'Search across large data' },
                ].map((s, i) => (
                  <Grid item xs={6} key={i}>
                    <Card sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                      <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, fontSize: '1.8rem', color: 'secondary.light', lineHeight: 1 }}>{s.value}</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>{s.label}</Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Tech stack */}
          <Box sx={{ mt: 6, pt: 5, borderTop: '1px solid rgba(255,255,255,0.15)', textAlign: 'center' }}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2, display: 'block' }}>
              Cloud-native, horizontally scalable architecture
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {techStack.map((tech, i) => (
                <Box key={i} sx={{ px: 1.75, py: 0.75, borderRadius: 999, border: '1px solid rgba(255,255,255,0.25)', fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)' }}>
                  {tech}
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>Flexible Pricing</SectionLabel>
          <Typography variant="h2" gutterBottom>Models that fit how you operate</Typography>
        </Box>
        <Grid container spacing={3}>
          {pricing.map((p, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ p: 3.5, height: '100%', display: 'flex', flexDirection: 'column', borderTop: '3px solid', borderTopColor: 'secondary.main' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>{p.model}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{p.structure}</Typography>
                <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid rgba(15,39,51,0.08)' }}>
                  <Typography variant="caption" sx={{ color: 'secondary.dark', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Best for</Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>{p.best}</Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 4 }}>
            <Box>
              <Typography variant="h3" gutterBottom sx={{ color: 'white' }}>Ready to digitize your inspection operation?</Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
                InspectIQ is a deployable, tested platform — not a prototype. Book a demo and see it on your workflows.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/contact')}
              sx={{ bgcolor: 'white', color: 'primary.main', px: 4, py: 1.5, whiteSpace: 'nowrap', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
            >
              Request a Demo
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Platform;
